import type React from 'react';
import { Navigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { ClientCategorisation } from '@/constants';
import { InstitutionFuturesProgressStep } from '@/context/progress';
import { RouteConfigProvider, useRouteConfig } from '@/context/route-config';
import FuturesClientCategorisationProfessionalAgreement, {
  type FuturesClientCategorisationProfessionalAgreementRouteConfig,
} from '@/features/futures/components/client-categorisation-professional-agreement';
import FuturesClientCategorisationLayout from '@/features/futures/layouts/client-categorisation';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMeta } from '@/hooks/queries/use-profile-institution-details';

const InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLayout = (props: React.ComponentPropsWithoutRef<typeof FuturesClientCategorisationLayout>) => {
  return <FuturesClientCategorisationLayout progress={0.9} step={InstitutionFuturesProgressStep.CLIENT_CATEGORISATION} {...props} />;
};
InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLayout.displayName = 'InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLayout';

const InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLoading = () => {
  return (
    <InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLayout>
      <LoadingCard />
    </InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLayout>
  );
};
InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLoading.displayName = 'InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLoading';

const InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementContent = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const {
    isLoading: isLoadingInstitutionDetailsMeta,
    data: { clientCategorisation, isRegulated },
  } = useProfileInstitutionDetailsMeta();
  const routeConfig = useRouteConfig<FuturesClientCategorisationProfessionalAgreementRouteConfig>();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsMeta;
  const countryOfIncorporation = institutionDetailsAnswers?.incorporation?.countryOfIncorporation;

  if (isLoading) return <InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLoading />;

  if (!countryOfIncorporation) throw new Error('Unable to proceed with client categorisation, no country of incorporation');

  if (!clientCategorisation) throw new Error('Unable to proceed with client categorisation, no client categorisation');

  if (clientCategorisation !== ClientCategorisation.PROFESSIONAL) {
    return <Navigate to={routeConfig.clientCategorisationTreatment} replace />;
  }

  return (
    <InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLayout>
      <FuturesClientCategorisationProfessionalAgreement countryOfResidency={countryOfIncorporation} reprocessFuturesEligibility={isRegulated ?? undefined} />
    </InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementLayout>
  );
};
InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementContent.displayName = 'InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementContent';

function InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementPage() {
  return (
    <RouteConfigProvider
      config={{
        appropriatenessTest: '/institution/onboarding/futures/appropriateness-test',
        clientCategorisationTreatment: '/institution/onboarding/futures/client-categorisation/treatment',
        jurisdictionUnavailable: '/institution/onboarding/futures/client-categorisation/jurisdiction-unavailable',
      }}
    >
      <InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementContent />
    </RouteConfigProvider>
  );
}
InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementPage.displayName = 'InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementPage';

export default InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementPage;
